import { useState } from "react";
import { BasicButton, Link, Modal, PrimaryButton, Text } from "@certn/cascadia";
import { InferClientPortalAPI } from "@certn/api";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@certn/cascadia-react";
import { useTranslation } from "react-i18next";
import { useHref, useNavigate } from "react-router-dom";

import { BILLING_MAILTO, OPEN_INVOICES_PATH, POPUP } from "./utils";

type BillingNoticesData = NonNullable<
  InferClientPortalAPI<"/api/internal/client-portal/customer-accounts/billing-notices/">
>;
type OverdueProfile = BillingNoticesData["overdue_profiles"][number];

const POPUP_SEVERITY: Record<string, number> = {
  step_15: 2,
  step_40: 4,
  step_45: 5,
};

interface Props {
  profiles: OverdueProfile[];
}

export function OverdueBillingPopup({ profiles }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  function getMostSeverePopup(
    profiles: OverdueProfile[]
  ): OverdueProfile | null {
    const popups = profiles.filter((profile) => profile.surface === POPUP);
    if (popups.length === 0) {
      return null;
    }
    return popups.reduce((best, profile) => {
      const rank = POPUP_SEVERITY[profile.marker] ?? 0;
      const bestRank = POPUP_SEVERITY[best.marker] ?? 0;
      if (rank !== bestRank) {
        return rank > bestRank ? profile : best;
      }
      if (profile.days_overdue !== best.days_overdue) {
        return profile.days_overdue > best.days_overdue ? profile : best;
      }
      return profile.billing_profile_id < best.billing_profile_id
        ? profile
        : best;
    });
  }

  const popup = dismissed ? null : getMostSeverePopup(profiles);

  if (!popup) {
    return null;
  }

  return (
    <Modal
      isOpen
      testID="overdue-popup-modal"
      onOpenChange={(isOpen) => setDismissed(!isOpen)}
    >
      <Dialog>
        {() => (
          <>
            <DialogHeader
              testID="overdue-popup"
              onClose={() => setDismissed(true)}
            >
              {popup.title}
            </DialogHeader>
            <DialogContent>
              <MessageWithInvoiceLink popup={popup} />
            </DialogContent>
            <DialogFooter>
              <BasicButton
                testID="overdue-popup-contact-support"
                onPress={() => {
                  window.location.href = BILLING_MAILTO;
                }}
              >
                {t("Contact support")}
              </BasicButton>
              <PrimaryButton
                testID="overdue-popup-pay-now"
                onPress={() => navigate(OPEN_INVOICES_PATH)}
              >
                {t("Pay now")}
              </PrimaryButton>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </Modal>
  );
}

function MessageWithInvoiceLink({ popup }: { popup: OverdueProfile }) {
  const message = popup.message ?? "";
  const invoiceNumber = popup.invoice_number;
  const invoiceId = popup.invoice_id;
  const token = `#${invoiceNumber}`;
  const index = invoiceId && invoiceNumber ? message.indexOf(token) : -1;
  const href = useHref(`/billing/invoices/current/${invoiceId}`);

  if (index === -1) {
    return (
      <Text color="text-base" style="global/body-primary">
        {message}
      </Text>
    );
  }

  return (
    <Text color="text-base" style="global/body-primary">
      {message.slice(0, index)}
      <Link
        href={href}
        isNewTab
        testID="overdue-popup-invoice-link"
        textStyle="global/body-primary"
      >
        {token}
      </Link>
      {message.slice(index + token.length)}
    </Text>
  );
}
